import _ from 'lodash'

const defaultStates = {
    create_campaign: {
        is_open: false,
    },
    inquiry_preview: {
        is_open: false,
        inquiry: null,
        preview: null,
        options: {},
    },
    create_campaign_module: {
        supercampaign_id: null,
        is_open: false,
        selected_campaign_type: null,
    },
    create_organization: {
        is_open: false,
    },
    confirm_pending_modal: {
        is_open: false,
    },
    participant: {
        is_open: false,
        creator: null,
        campaign: null,
    },
    creator: {
        is_open: false,
        platform_id: null,
        platform_user_id: null,
        supercampaign_id: null,
        campaign_id: null,
        participant_id: null,
        participant: null,
        campaign: null,
        is_limited_mode: false,
        is_in_workflow: false,
    },
    add_creator_to_modal: {
        is_open: false,
        superviews: null,
        platform_id: null,
        default_all_platforms: false,
        creator_platforms: null,
    },
    export_table: {
        is_open: false,
        columns: {
            all: null,
            enabled: null,
        },
        rows: null,
        file_name: null,
        providerFn: null,
        checkerFn: null,
        rowCount: null,
    },
    reveal_emails: {
        is_open: false,
        creators: null,
    },
    conversation_view: {
        is_open: false,
        conversation: null,
    },
    email_compose: {
        is_open: false,
        recipient: null,
        recipients: [],
        subject: null,
        replyToMessage: null,
        replyToConversation: null,
        creatorMode: false,
    },
    creator_stack: [],
}

export default {
    state: _.cloneDeep(defaultStates),

    /* eslint-disable no-param-reassign */
    mutations: {
        'modal:open': function (state, modalKey) {
            if (!Object.hasOwn(state, modalKey)) {
                console.error('Tried to open modal that does not exist', modalKey)
                return
            }

            state[modalKey].is_open = true
        },

        'modal:close': function (state, modalKey) {
            if (!Object.hasOwn(state, modalKey)) {
                console.error('Tried to open modal that does not exist', modalKey)
                return
            }

            // Special handling for creator modal to support stacking
            if (modalKey === 'creator') {
                if (state.creator_stack.length > 0) {
                    // Pop last item from stack
                    state.creator_stack.pop()
                }
                // Also reset old single modal state for backwards compatibility
                if (state.creator_stack.length === 0) {
                    state[modalKey].is_open = false
                    state[modalKey] = _.cloneDeep(defaultStates[modalKey])
                }
                return
            }

            state[modalKey].is_open = false
            state[modalKey] = _.cloneDeep(defaultStates[modalKey])
        },

        'modal:toggleVisible': function (state, modalKey) {
            if (!Object.hasOwn(state, modalKey)) {
                console.error('Tried to open modal that does not exist', modalKey)
                return
            }

            state[modalKey].is_open = !state[modalKey].is_open
        },

        'modal:setData': function (state, { modal_id: modalId, data }) {
            if (!Object.hasOwn(state, modalId)) {
                console.error('Tried to open modal that does not exist', modalId)
                return
            }

            // Special handling for creator modal to support stacking
            if (modalId === 'creator') {
                // Normalize prop names to camelCase for CreatorProfile.vue
                const normalizedData = {
                    isOpen: data.is_open !== undefined ? data.is_open : true,
                    platformId: data.platform_id || data.platformId,
                    platformUserId: data.platform_user_id || data.platformUserId,
                    supercampaignId: data.supercampaign_id || data.supercampaignId,
                    campaignId: data.campaign_id || data.campaignId,
                    participantId: data.participant_id || data.participantId,
                    participant: data.participant,
                    campaign: data.campaign,
                    isLimitedMode: data.is_limited_mode || data.isLimitedMode,
                    isInWorkflow: data.is_in_workflow || data.isInWorkflow,
                    targetingGroups: data.targeting_groups || data.targetingGroups,
                    initialMetricWindow: data.initial_metric_window || data.initialMetricWindow,
                }

                // Remove undefined values
                Object.keys(normalizedData).forEach((key) => {
                    if (normalizedData[key] === undefined) {
                        delete normalizedData[key]
                    }
                })

                // Check if we're adding a duplicate (same profile at top of stack)
                const topProfile = state.creator_stack[state.creator_stack.length - 1]
                const isDuplicate =
                    topProfile &&
                    topProfile.platformId === normalizedData.platformId &&
                    topProfile.platformUserId === normalizedData.platformUserId

                if (isDuplicate) {
                    // Merge new data into existing top item (allows enriching with additional props)
                    Object.assign(topProfile, normalizedData)
                } else {
                    // Push the new profile to the stack
                    state.creator_stack.push(normalizedData)
                }
                return
            }

            _.forEach(data, (value, fieldKey) => {
                if (Object.hasOwn(state[modalId], fieldKey)) {
                    state[modalId][fieldKey] = value
                }
            })
        },

        'modal:pushToStack': function (state, profile) {
            // Check for duplicates
            const topProfile = state.creator_stack[state.creator_stack.length - 1]
            const isDuplicate =
                topProfile &&
                topProfile.platformId === profile.platformId &&
                topProfile.platformUserId === profile.platformUserId

            // Check max depth
            if (state.creator_stack.length >= 10) {
                console.warn('Maximum stack depth (10) reached')
                return
            }

            if (!isDuplicate) {
                state.creator_stack.push({ ...profile, isOpen: true })
            }
        },

        'modal:popFromStack': function (state, index = -1) {
            if (state.creator_stack.length === 0) {
                return
            }

            if (index === -1) {
                // Pop last item
                state.creator_stack.pop()
            } else {
                // Remove specific item
                state.creator_stack.splice(index, 1)
            }
        },

        'modal:clearStack': function (state) {
            state.creator_stack = []
        },
    },
    /* eslint-enable no-param-reassign */
    getters: {
        getModal: (state) => (modalKey) => {
            if (!Object.hasOwn(state, modalKey)) {
                console.error('Tried to open modal that does not exist', modalKey)
                return undefined
            }

            return state[modalKey]
        },

        modals: (state) => state,

        creatorStack: (state) => state.creator_stack,

        hasStackedCreators: (state) => state.creator_stack.length > 0,

        topCreator: (state) => {
            if (state.creator_stack.length === 0) {
                return null
            }
            return state.creator_stack[state.creator_stack.length - 1]
        },
    },
}
